import React, { useState } from 'react'
import { useQuery, useMutation } from '@cedarjs/web'
import {
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Collapse,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import AddIcon from '@mui/icons-material/Add'
import { INTEGRATION_REGISTRATIONS_QUERY } from 'src/pages/AdminIntegrationsPage/queries/integrationRegistrations'

const CREATE_INTEGRATION_REGISTRATION_MUTATION = gql`
  mutation CreateIntegrationRegistration(
    $input: CreateIntegrationRegistrationInput!
  ) {
    createIntegrationRegistration(input: $input) {
      id
      enabled
      validated
    }
  }
`

const UPDATE_INTEGRATION_REGISTRATION_OPERATION_MUTATION = gql`
  mutation UpdateIntegrationRegistrationOperation(
    $id: String!
    $input: UpdateIntegrationRegistrationOperationInput!
  ) {
    updateIntegrationRegistrationOperation(id: $id, input: $input) {
      id
      enabled
    }
  }
`

interface AircraftRegistration {
  aircraft: {
    id: string
    tailNumber: string
    name: string
    orgSlug: string
  }
  registrations: Array<{
    id: string
    enabled: boolean
    validated: boolean
    integration: {
      id: string
      name: string
      displayName: string
    }
    IntegrationRegistrationOperation: Array<{
      id: string
      enabled: boolean
      operation: {
        id: string
        operationName: string
        description: string
      }
    }>
  }>
}

interface OrganizationGroup {
  orgSlug: string
  aircraft: Record<string, AircraftRegistration>
}

const AircraftIntegrationsList = () => {
  const aircraftsOrgSlug = 'all'

  const { data, loading, error, refetch } = useQuery(
    INTEGRATION_REGISTRATIONS_QUERY,
    {
      variables: {
        aircraftsOrgSlug,
      },
    }
  )

  const [expandedOrg, setExpandedOrg] = useState<string | null>(null)
  const [expandedAircraft, setExpandedAircraft] = useState<string | null>(null)
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [selectedAircraft, setSelectedAircraft] = useState('')
  const [selectedIntegration, setSelectedIntegration] = useState('')
  const [selectedOperations, setSelectedOperations] = useState<
    Record<string, boolean>
  >({})
  const [existingOperations, setExistingOperations] = useState<
    Record<string, boolean>
  >({})

  const [createRegistration] = useMutation(
    CREATE_INTEGRATION_REGISTRATION_MUTATION,
    {
      onCompleted: () => {
        setIsAddDialogOpen(false)
        refetch()
      },
    }
  )

  const [updateOperation] = useMutation(
    UPDATE_INTEGRATION_REGISTRATION_OPERATION_MUTATION,
    {
      onCompleted: () => {
        refetch()
      },
    }
  )

  if (loading) {
    return <Typography>Loading aircraft integrations...</Typography>
  }

  if (error) {
    return (
      <Typography color="error">
        Error loading aircraft integrations: {error.message}
      </Typography>
    )
  }

  if (!data?.integrationRegistrations) {
    return <Typography>No integration registrations found.</Typography>
  }

  // Group registrations by organization and then by aircraft
  const registrationsByOrg: Record<string, OrganizationGroup> =
    data.integrationRegistrations.reduce((acc, reg) => {
      const orgSlug = reg.aircraft.orgSlug
      if (!acc[orgSlug]) {
        acc[orgSlug] = {
          orgSlug,
          aircraft: {},
        }
      }
      if (!acc[orgSlug].aircraft[reg.aircraft.id]) {
        acc[orgSlug].aircraft[reg.aircraft.id] = {
          aircraft: reg.aircraft,
          registrations: [],
        }
      }
      acc[orgSlug].aircraft[reg.aircraft.id].registrations.push(reg)
      return acc
    }, {} as Record<string, OrganizationGroup>)

  const handleToggleOrg = (orgSlug: string) => {
    setExpandedOrg(expandedOrg === orgSlug ? null : orgSlug)
  }

  const handleToggleAircraft = (aircraftId: string) => {
    setExpandedAircraft(expandedAircraft === aircraftId ? null : aircraftId)
  }

  const handleAddIntegration = () => {
    if (!selectedAircraft || !selectedIntegration) return

    createRegistration({
      variables: {
        input: {
          aircraftId: selectedAircraft,
          integrationId: selectedIntegration,
          validated: false,
          enabled: true,
          externalMetadata: {},
        },
      },
    })
  }

  const handleToggleOperation = (operationId: string, enabled: boolean) => {
    updateOperation({
      variables: {
        id: operationId,
        input: {
          enabled,
        },
      },
    })
  }

  const handleOpenAddDialog = () => {
    setSelectedAircraft('')
    setSelectedIntegration('')
    setSelectedOperations({})
    setIsAddDialogOpen(true)
  }

  const handleIntegrationChange = (integrationId: string) => {
    setSelectedIntegration(integrationId)
    const integration = data.integrations.find((i) => i.id === integrationId)
    if (integration) {
      const existingRegistration = data.integrationRegistrations.find(
        (reg) =>
          reg.aircraft.id === selectedAircraft &&
          reg.integration.id === integrationId
      )

      const operations: Record<string, boolean> = {}
      if (existingRegistration) {
        existingRegistration.IntegrationRegistrationOperation.forEach((op) => {
          operations[op.operation.id] = op.enabled
        })
        setExistingOperations(operations)
      } else {
        setExistingOperations({})
        integration.IntegrationOperation.forEach((op) => {
          operations[op.id] = false
        })
      }
      setSelectedOperations(operations)
    }
  }

  return (
    <>
      <Box sx={{ mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenAddDialog}
        >
          Add Integration
        </Button>
      </Box>

      <List>
        {Object.values(registrationsByOrg).map(({ orgSlug, aircraft }) => (
          <React.Fragment key={orgSlug}>
            <ListItem>
              <ListItemText
                primary={orgSlug}
                secondary={`${Object.keys(aircraft).length} aircraft`}
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  onClick={() => handleToggleOrg(orgSlug)}
                  aria-label="expand organization"
                >
                  {expandedOrg === orgSlug ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  )}
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
            <Collapse in={expandedOrg === orgSlug}>
              <List component="div" disablePadding>
                {Object.values(aircraft).map(({ aircraft, registrations }) => (
                  <React.Fragment key={aircraft.id}>
                    <ListItem sx={{ pl: 4 }}>
                      <ListItemText
                        primary={`${aircraft.tailNumber} - ${aircraft.name}`}
                        secondary={`${
                          registrations.length
                        } integration(s) with ${registrations.reduce(
                          (total, reg) =>
                            total + reg.IntegrationRegistrationOperation.length,
                          0
                        )} operation(s)`}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={() => handleToggleAircraft(aircraft.id)}
                          aria-label="expand aircraft"
                        >
                          {expandedAircraft === aircraft.id ? (
                            <ExpandLessIcon />
                          ) : (
                            <ExpandMoreIcon />
                          )}
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                    <Collapse in={expandedAircraft === aircraft.id}>
                      <List component="div" disablePadding>
                        {registrations.map((registration) => (
                          <ListItem key={registration.id} sx={{ pl: 8 }}>
                            <ListItemText
                              primary={
                                registration.integration.displayName ||
                                registration.integration.name
                              }
                              secondary={
                                <Box>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Status:{' '}
                                    {registration.validated
                                      ? 'Validated'
                                      : 'Not Validated'}{' '}
                                    |{' '}
                                    {registration.enabled
                                      ? 'Enabled'
                                      : 'Disabled'}
                                  </Typography>
                                  <FormGroup>
                                    {registration.IntegrationRegistrationOperation.map(
                                      (regOp) => (
                                        <FormControlLabel
                                          key={regOp.id}
                                          control={
                                            <Checkbox
                                              checked={regOp.enabled}
                                              onChange={(e) =>
                                                handleToggleOperation(
                                                  regOp.id,
                                                  e.target.checked
                                                )
                                              }
                                            />
                                          }
                                          label={`${
                                            regOp.operation.operationName
                                          }${
                                            regOp.operation.description
                                              ? ` - ${regOp.operation.description}`
                                              : ''
                                          }`}
                                        />
                                      )
                                    )}
                                  </FormGroup>
                                </Box>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  </React.Fragment>
                ))}
              </List>
            </Collapse>
          </React.Fragment>
        ))}
      </List>

      <Dialog open={isAddDialogOpen} onClose={() => setIsAddDialogOpen(false)}>
        <DialogTitle>Add Integration to Aircraft</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Aircraft</InputLabel>
              <Select
                value={selectedAircraft}
                label="Aircraft"
                onChange={(e) => setSelectedAircraft(e.target.value)}
              >
                {data.aircrafts.map((aircraft) => (
                  <MenuItem key={aircraft.id} value={aircraft.id}>
                    {aircraft.tailNumber} - {aircraft.orgSlug}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Integration</InputLabel>
              <Select
                value={selectedIntegration}
                label="Integration"
                onChange={(e) => handleIntegrationChange(e.target.value)}
              >
                {data.integrations.map((integration) => (
                  <MenuItem key={integration.id} value={integration.id}>
                    {integration.displayName || integration.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedIntegration && (
              <FormGroup>
                <Typography variant="subtitle2" gutterBottom>
                  Available Operations
                </Typography>
                {data.integrations
                  .find((i) => i.id === selectedIntegration)
                  ?.IntegrationOperation.map((operation) => {
                    const isExisting = existingOperations[operation.id]
                    return (
                      <FormControlLabel
                        key={operation.id}
                        control={
                          <Checkbox
                            checked={selectedOperations[operation.id] || false}
                            onChange={(e) =>
                              setSelectedOperations({
                                ...selectedOperations,
                                [operation.id]: e.target.checked,
                              })
                            }
                            disabled={isExisting}
                          />
                        }
                        label={
                          <Typography
                            sx={{
                              color: isExisting
                                ? 'text.disabled'
                                : 'text.primary',
                            }}
                          >
                            {`${operation.operationName}${
                              operation.description
                                ? ` - ${operation.description}`
                                : ''
                            }${isExisting ? ' (Already enabled)' : ''}`}
                          </Typography>
                        }
                      />
                    )
                  })}
              </FormGroup>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsAddDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAddIntegration}
            variant="contained"
            disabled={!selectedAircraft || !selectedIntegration}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default AircraftIntegrationsList
